import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Check, Zap, Crown, Gem, Star, Sparkles, Building2 } from "lucide-react";
import { getAllSubscriptionPlans } from "../../../../api/action/subscription.jsx";
import { useFlexOfficeCTA } from "./useFlexOfficeCTA";
import data from "../data/flexOfficeData.json";
import { mergePricingPlansWithApi } from "../../shared/pricingUtils";
import { academyLogger } from "utils/logger";

const ICON_MAP = { Zap, Crown, Gem };

const useBreakpoint = () => {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return { isMobile: w < 640, isTablet: w >= 640 && w < 1024 };
};

const FlexOfficePricing = () => {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [pricingUnavailable, setPricingUnavailable] = useState(false);
  const dispatch = useDispatch();
  const cta = useFlexOfficeCTA();
  const { isMobile, isTablet } = useBreakpoint();
  const d = data.pricing;

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      try {
        const apiPlans = await dispatch(getAllSubscriptionPlans({
          businessDomain: "FLEX_OFFICE"
        }));

        if (!isMounted) {
          return;
        }

        const mappedPlans = mergePricingPlansWithApi({
          apiPlans,
          fallbackPlans: d.plans,
          businessDomain: "FLEX_OFFICE",
          tierOrder: ["STARTER", "GROWTH", "AUTHORITY"],
        });

        setPlans(mappedPlans);
        setPricingUnavailable(mappedPlans.length === 0);
      } catch (error) {
        academyLogger.error("Failed to load flex office pricing plans:", error);
        setPlans([]);
        setPricingUnavailable(true);
      }
    };

    loadPlans();

    return () => {
      isMounted = false;
    };
  }, [d.plans, dispatch]);

  return (
    <section id="flexoffice-pricing" style={{ background: "white", padding: isMobile ? "60px 20px" : isTablet ? "72px 32px" : "96px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 40 : 56 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#eff6ff", border: "1px solid #bfdbfe", padding: "8px 18px", borderRadius: 100, marginBottom: 20 }}>
            <Sparkles style={{ width: 14, height: 14, color: "#1d4ed8" }} />
            <span style={{ color: "#1d4ed8", fontSize: 13, fontWeight: 700, letterSpacing: "0.05em" }}>{d.badge}</span>
          </div>
          <h2 style={{ fontSize: isMobile ? 28 : isTablet ? 38 : 48, fontWeight: 900, letterSpacing: "-0.02em", color: "#0f172a", lineHeight: 1.1, marginBottom: 12 }}>{d.headline}</h2>
          <p style={{ fontSize: isMobile ? 15 : 18, color: "#64748b", marginBottom: 28 }}>{d.description}</p>

          <div style={{ display: "inline-flex", alignItems: "center", background: "#f1f5f9", borderRadius: 100, padding: 4 }}>
            {["monthly", "annual"].map(cycle => (
              <button key={cycle} onClick={() => setBillingCycle(cycle)}
                style={{ padding: isMobile ? "8px 18px" : "9px 24px", borderRadius: 100, fontSize: isMobile ? 13 : 14, fontWeight: 600, border: "none", cursor: "pointer", transition: "all 0.2s", background: billingCycle === cycle ? "#1d4ed8" : "transparent", color: billingCycle === cycle ? "white" : "#64748b", display: "flex", alignItems: "center", gap: 8 }}>
                {cycle === "monthly" ? "Monthly" : "Annual"}
                {cycle === "annual" && <span style={{ background: "#d1fae5", color: "#065f46", padding: "2px 8px", borderRadius: 100, fontSize: 11, fontWeight: 700 }}>-20%</span>}
              </button>
            ))}
          </div>
        </div>

        {!pricingUnavailable && plans.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? 20 : 24, alignItems: "center" }}>
          {plans.map((plan, i) => {
            const IconComp = ICON_MAP[plan.icon];
            // Authority plan → talkToSales; others → getStarted
            const handlePlanClick = plan.name === "Authority" ? cta.talkToSales : cta.getStarted;
            const btnLabel = plan.name === "Authority" ? "Contact Sales" : "Get Started";

            return (
              <div key={i} style={{ position: "relative", transform: plan.highlight && !isMobile && !isTablet ? "scale(1.03)" : "scale(1)" }}>
                {plan.popularBadge && (
                  <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: "#1d4ed8", color: "white", padding: "6px 18px", borderRadius: 100, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 4px 16px rgba(29,78,216,0.35)", zIndex: 1 }}>
                    <Star style={{ width: 12, height: 12, fill: "white" }} /> {plan.popularBadge}
                  </div>
                )}
                <div style={{ background: "white", border: plan.highlight ? "2px solid #1d4ed8" : "1.5px solid #e2e8f0", borderRadius: 20, padding: isMobile ? "28px 20px" : "36px 30px", height: "100%", display: "flex", flexDirection: "column", boxShadow: plan.highlight ? "0 16px 60px rgba(29,78,216,0.15)" : "0 2px 12px rgba(0,0,0,0.04)" }}>
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "center", background: plan.highlight ? "#1d4ed8" : "#f1f5f9", color: plan.highlight ? "white" : "#64748b" }}>
                      {IconComp && <IconComp style={{ width: 20, height: 20 }} />}
                    </div>
                    <h3 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: "#0f172a" }}>{plan.name}</h3>
                    <p style={{ color: "#64748b", fontSize: isMobile ? 13 : 14, marginTop: 4 }}>{plan.subtitle}</p>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                      <span style={{ fontSize: isMobile ? 36 : 46, fontWeight: 900, color: "#0f172a" }}>{billingCycle === "monthly" ? plan.price : plan.yearlyPrice}</span>
                      <span style={{ color: "#64748b", fontSize: 16 }}>/{billingCycle === "monthly" ? "mo" : "yr"}</span>
                    </div>
                    {billingCycle === "annual" && <p style={{ color: "#22c55e", fontSize: 13, fontWeight: 600, marginTop: 4 }}>Save 20% with annual billing</p>}
                    {plan.setupFeeText && <p style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>+ {plan.setupFeeText}</p>}
                  </div>

                  <div style={{ flex: 1, marginBottom: 24 }}>
                    {plan.features.map((feat, fi) => (
                      <div key={fi} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                        <div style={{ width: 20, height: 20, background: "#f0fdf4", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                          <Check style={{ width: 13, height: 13, color: "#22c55e" }} />
                        </div>
                        <span style={{ color: "#374151", fontSize: isMobile ? 13 : 14 }}>{feat}</span>
                      </div>
                    ))}
                  </div>

                  {/* ── Per-plan CTA ── */}
                  <button
                    onClick={handlePlanClick}
                    style={{ width: "100%", padding: "13px 0", borderRadius: 12, fontSize: isMobile ? 14 : 15, fontWeight: 700, border: "none", cursor: "pointer", transition: "all 0.25s", background: plan.highlight ? "#1d4ed8" : "#f1f5f9", color: plan.highlight ? "white" : "#374151" }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.background = plan.highlight ? "#1e3a8a" : "#e2e8f0"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.background = plan.highlight ? "#1d4ed8" : "#f1f5f9"; }}>
                    {btnLabel}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        ) : (
          <div style={{ textAlign: "center", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 20, padding: isMobile ? "28px 20px" : "36px 24px" }}>
            <h3 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>Pricing is currently unavailable</h3>
            <p style={{ color: "#64748b", fontSize: 15, lineHeight: 1.7, maxWidth: 560, margin: "0 auto 20px" }}>
              We could not load the latest FlexOffice subscription pricing right now. Please contact our team for current plan details.
            </p>
            <button
              onClick={cta.talkToSales}
              style={{ background: "#1d4ed8", color: "white", border: "none", padding: "12px 28px", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
            >
              Talk to Sales
            </button>
          </div>
        )}

        {/* ── Enterprise banner — "Talk to Sales" ── */}
        <div style={{ background: "linear-gradient(135deg, #0f172a, #1e293b)", borderRadius: 24, padding: isMobile ? "40px 24px" : isTablet ? "48px 36px" : "56px 48px", marginTop: isMobile ? 32 : 48, textAlign: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)", backgroundSize: "40px 40px" }} />
          <div style={{ position: "relative" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(29,78,216,0.2)", border: "1px solid rgba(29,78,216,0.3)", color: "#93c5fd", padding: "7px 18px", borderRadius: 100, fontSize: 12, fontWeight: 700, marginBottom: 20, letterSpacing: "0.05em" }}>
              <Building2 style={{ width: 14, height: 14 }} /> {d.enterprise.badge}
            </div>
            <h3 style={{ fontSize: isMobile ? 22 : 34, fontWeight: 900, color: "white", marginBottom: 14 }}>{d.enterprise.headline}</h3>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: isMobile ? 14 : 17, maxWidth: 520, margin: "0 auto 32px", lineHeight: 1.7 }}>{d.enterprise.description}</p>
            {/* ── Talk to Sales ── */}
            <button
              onClick={cta.talkToSales}
              style={{ background: "#1d4ed8", color: "white", border: "none", padding: isMobile ? "12px 28px" : "14px 36px", borderRadius: 12, fontSize: isMobile ? 14 : 15, fontWeight: 700, cursor: "pointer", transition: "all 0.25s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#1e3a8a"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#1d4ed8"; e.currentTarget.style.transform = "none"; }}>
              {d.enterprise.button}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FlexOfficePricing;
